import AdminRezeptSheetListTemplate from '@/components/templates/AdminRezeptSheetListTemplate';
import CreateRezeptSheetModal from '@/components/organisms/CreateRezeptSheetModal';
import EditRezeptSheetModal from '@/components/organisms/EditRezeptSheetModal';
import { useAdminRezeptSheetList } from '@/hooks/admin/useAdminRezeptSheetList';
import { downloadRezeptSheetsCsv } from '@/services/admin/rezeptSheetService';
import { useRedirectWhenPageEmpty } from '@/hooks/useRedirectWhenPageEmpty';
import useAppToast from '@/hooks/useAppToast';
import type { RezeptSheet } from '@/types/admin-rezept-sheet';
import type { SorterResult } from 'antd/es/table/interface';
import { useSearchParams, useParams } from 'react-router-dom';
import { useState } from 'react';
import { CSV_SUCCESS_MESSAGE, CSV_ERROR_MESSAGE } from '@/constants/messages';

const SiteAdminRezeptSheetsPage = () => {
  const { site } = useParams<{ site: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const page = searchParams.get('page') || '1';
  const sortByParam = searchParams.get('sort_by') || undefined;
  const orderParam = (searchParams.get('order') as 'ascend' | 'descend') || undefined;

  const [csvLoading, setCsvLoading] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const toast = useAppToast();

  const { data, isLoading, isError: listError } = useAdminRezeptSheetList({
    page: parseInt(page, 10) || 1,
    per_page: 20,
    sort_by: sortByParam,
    order: orderParam,
  });

  const listData = data?.data ?? [];
  useRedirectWhenPageEmpty({
    searchParams,
    setSearchParams,
    isLoading,
    isError: listError,
    dataLength: listData.length,
  });

  const breadcrumbItems = [{ title: 'レセプト用紙' }];

  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', newPage.toString());
    setSearchParams(params);
  };

  const handleTableChange = (
    sorter: SorterResult<RezeptSheet> | SorterResult<RezeptSheet>[]
  ) => {
    const params = new URLSearchParams(searchParams);
    const singleSorter = Array.isArray(sorter) ? sorter[0] : sorter;

    if (singleSorter.order) {
      params.set('sort_by', (singleSorter.field || singleSorter.columnKey) as string);
      params.set('order', singleSorter.order);
    } else {
      params.delete('sort_by');
      params.delete('order');
    }
    params.set('page', '1');
    setSearchParams(params);
  };

  const handleCsvDownload = async () => {
    if (!site) return;
    setCsvLoading(true);
    try {
      await downloadRezeptSheetsCsv(site, {
        sort_by: sortByParam,
        order: orderParam,
      });
      toast.success(CSV_SUCCESS_MESSAGE);
    } catch (err) {
      toast.error(CSV_ERROR_MESSAGE);
    } finally {
      setCsvLoading(false);
    }
  };

  return (
    <>
      <AdminRezeptSheetListTemplate
        data={listData}
        total={data?.meta.total ?? 0}
        current={data?.meta.current_page ?? 1}
        pageSize={data?.meta.per_page ?? 20}
        sortInfo={{ columnKey: sortByParam, order: orderParam }}
        isLoading={isLoading}
        onChange={handlePageChange}
        onTableChange={handleTableChange}
        breadcrumbItems={breadcrumbItems}
        onCreateClick={() => setCreateModalOpen(true)}
        onEditClick={(id) => setEditingId(id)}
        onCsvDownload={handleCsvDownload}
        csvLoading={csvLoading}
      />
      <CreateRezeptSheetModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
      />
      <EditRezeptSheetModal
        id={editingId}
        isOpen={editingId != null}
        onClose={() => setEditingId(null)}
      />
    </>
  );
};

export default SiteAdminRezeptSheetsPage;
